import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronRight, CheckCircle2, FileText, Clock, Package, Truck, Megaphone, Zap, Shield, Target } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const BlinkitServices = () => {
  const requiredDocuments = [
    'GST Certificate',
    'PAN Card',
    'Trademark Certificate (if available)',
    'Mobile Number',
    'Email ID'
  ];

  const whyChooseUs = [
    { icon: FileText, title: 'Document Assistance', description: 'Complete guidance on required documentation.' },
    { icon: Package, title: 'Product Listing Support', description: 'EAN-based catalog preparation and listing.' },
    { icon: Truck, title: 'Dark Store Integration', description: 'Smooth inventory transfer to Blinkit dark stores.' },
    { icon: Megaphone, title: 'Advertising Setup', description: 'Campaign setup to boost visibility and sales.' }
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Blinkit Seller Onboarding Services | Start Selling on Blinkit</title>
        <meta name="description" content="Complete Blinkit seller onboarding: documentation, brand approval & product listing. ₹25,000/SKU includes ad credits. Quick commerce onboarding experts." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/blinkit-seller-onboarding" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-100 py-4 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-slate-700 hover:text-primary font-medium">Home</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/services" className="text-slate-700 hover:text-primary font-medium">Services</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/quick-commerce-account-management" className="text-slate-700 hover:text-primary font-medium">Quick Commerce</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="text-primary font-semibold">Blinkit Onboarding</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className="py-16 md:py-24 lg:py-28 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 213, 0, 0.05) 0%, rgba(255, 193, 7, 0.05) 100%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
        }}
      >
        <div className="absolute top-20 right-10 w-72 h-72 bg-yellow-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center space-x-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap size={16} />
                <span>Quick Commerce Platform</span>
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6">
                Blinkit Seller <span className="bg-gradient-to-r from-yellow-500 to-amber-500 bg-clip-text text-transparent">Onboarding Services</span>
              </h1>
              <p className="text-lg leading-relaxed text-slate-700 mb-4 max-w-3xl mx-auto">
                Blinkit is a fast-growing quick commerce platform where brands can deliver products to customers within minutes through dark stores.
              </p>
              <p className="text-lg leading-relaxed text-slate-600 mb-4 max-w-3xl mx-auto">
                At Obiquiste Solutions, we help businesses complete the Blinkit seller onboarding process quickly and smoothly so they can start selling on the platform.
              </p>
              <div className="inline-flex items-center space-x-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm font-medium mb-8">
                <Clock size={16} />
                <span>Onboarding completed within 3–4 working days when documents are shared on time</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact?service=blinkit">
                  <Button className="bg-primary text-white hover:bg-primary-600 shadow-lg hover:shadow-xl px-8 py-6 rounded-full text-base font-semibold">
                    Start Blinkit Onboarding
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact?service=blinkit">
                  <Button variant="outline" className="border-2 border-slate-300 hover:border-primary hover:bg-slate-50 px-8 py-6 rounded-full text-base font-semibold">
                    Talk to Expert
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Documents Required */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-yellow-100 rounded-xl mb-6">
                <FileText className="w-7 h-7 text-yellow-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Documents Required for Blinkit Seller Onboarding
              </h2>
              <p className="text-lg text-slate-700">
                The following documents are required to start the onboarding process. Once these details are shared, the onboarding process can be initiated immediately.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Required Documents:</h3>
                <div className="space-y-4">
                  {requiredDocuments.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Blinkit Onboarding Process */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
              <Clock className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Blinkit Onboarding Process
            </h2>
            <p className="text-lg text-slate-700 mb-6">
              After receiving the documents, our team will start the onboarding process with Blinkit and coordinate the required steps for seller account activation.
            </p>
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm inline-block">
              <div className="flex items-center space-x-3">
                <CheckCircle2 className="w-6 h-6 text-green-500" />
                <span className="text-slate-700 font-medium">If documents are submitted correctly and on time, onboarding can be completed within 3–4 working days</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Listing on Blinkit */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-4 text-slate-800">What We Help With:</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Product catalog preparation with EAN codes</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Listing products on the Blinkit platform</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Ensuring catalog compliance with platform requirements</span>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-amber-800 text-sm">
                    <strong>Note:</strong> EAN codes are required to list products on Blinkit.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-purple-100 rounded-xl mb-6">
                <Package className="w-7 h-7 text-purple-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Product Listing on Blinkit
              </h2>
              <p className="text-lg text-slate-700">
                After the seller account is activated, we assist in listing products on the Blinkit platform. We help prepare the product catalog with proper EAN codes to ensure smooth listing.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Inventory Transfer to Dark Stores */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-xl mb-6">
                <Truck className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Inventory Transfer to Blinkit Dark Stores
              </h2>
              <p className="text-lg text-slate-700 mb-4">
                Once products are listed, we assist in sending the product to the Blinkit feeder system, which allows inventory to be transferred to Blinkit dark stores.
              </p>
              <p className="text-lg text-slate-700">
                This step ensures that products become available for delivery to customers within minutes.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold">1</span>
                  </div>
                  <span className="text-slate-700 font-medium">Products listed on Blinkit</span>
                </div>
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold">2</span>
                  </div>
                  <span className="text-slate-700 font-medium">Inventory sent to feeder system</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold">3</span>
                  </div>
                  <span className="text-slate-700 font-medium">Products available in dark stores for delivery</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Blinkit Advertising Setup */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-4 text-slate-800">Advertising Benefits:</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Improved product visibility on the platform</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Attract more customers to your products</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Generate conversions and improve sales</span>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-100 rounded-xl mb-6">
                <Megaphone className="w-7 h-7 text-orange-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Blinkit Advertising Setup
              </h2>
              <p className="text-lg text-slate-700">
                Once products go live on the platform, we help start Blinkit advertising campaigns to improve product visibility and generate conversions. Advertising helps attract customers and improve sales performance on the platform.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Obiquiste Solutions */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              End-to-End Blinkit Onboarding Support
            </h2>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto">
              Obiquiste Solutions provides complete Blinkit onboarding support including document assistance, product listings, dark store integration, and advertising setup.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {whyChooseUs.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 text-center"
              >
                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-slate-300 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands We Work With */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Target className="w-4 h-4 mr-2" />
              Our Clients
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Brands We've Onboarded on Blinkit
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We have successfully onboarded these brands on Blinkit, helping them tap into India's fastest-growing quick commerce market.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { name: 'GW Khamkar', category: 'Food Products' },
              { name: 'Vintage Oils', category: 'Edible Oils' },
              { name: 'Fitelo', category: 'Health Foods' },
              { name: 'RajGharana Metals', category: 'Kitchenware' },
              { name: 'HII Health Inside It', category: 'Health & Wellness' },
              { name: 'Auli Lifestyle', category: 'Beauty & Skincare' },
            ].map((brand, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white p-6 rounded-2xl border border-slate-200 text-center hover:shadow-lg hover:border-yellow-300 transition-all"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-yellow-100 to-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-yellow-600">{brand.name.charAt(0)}</span>
                </div>
                <h3 className="font-heading font-bold text-slate-800 mb-1">{brand.name}</h3>
                <p className="text-xs text-slate-500">{brand.category}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-yellow-50 to-amber-50 rounded-3xl p-12 border border-yellow-200">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Ready to Start Selling on Blinkit?
            </h2>
            <p className="text-lg text-slate-700 mb-8">
              Let us handle your Blinkit seller onboarding and get your products live on India's leading quick commerce platform.
            </p>
            <Link to="/contact?service=blinkit">
              <Button className="bg-primary text-white hover:bg-primary-600 px-10 py-7 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl">
                Start Blinkit Onboarding
                <ArrowRight className="ml-2" size={24} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlinkitServices;
